/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from "react";
import {
  X,
  Phone,
  Mail,
  MapPin,
  FileText,
  Star,
  User,
  Briefcase,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  Layers,
} from "lucide-react";

const ProfileModal = ({ isOpen, onClose, caregiver }) => {
  if (!isOpen || !caregiver) return null;

  // Helper function to safely get values
  const getValue = (value, defaultValue = "Not specified") => {
    return value !== null && value !== undefined && value !== ""
      ? value
      : defaultValue;
  };

  // Parse languages safely
  const languages = Array.isArray(caregiver.languages)
    ? caregiver.languages
    : caregiver.languages
      ? caregiver.languages
          .split(",")
          .map((l) => l.trim())
          .filter(Boolean)
      : ["Amharic", "English"];

  // Get category info from serviceCategory object
  const categoryLabel = caregiver.serviceCategory?.label || "Caregiver";
  const categoryIcon = caregiver.serviceCategory?.icon || "User";
  const categoryColor = caregiver.serviceCategory?.color || "#CE257A";

  // Dynamic field rendering function
  const renderDynamicFields = () => {
    if (!caregiver.fieldValues || caregiver.fieldValues.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h4 className="text-lg font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
          <Layers size={20} style={{ color: categoryColor }} /> {categoryLabel}{" "}
          Details
        </h4>
        <div className="bg-background dark:bg-dark-background p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border space-y-4">
          {caregiver.fieldValues.map((fieldValue) => {
            const field = fieldValue.field;
            if (!field) return null;

            const values = fieldValue.values || [];
            const hasValues = values.length > 0 && values.some((v) => v);

            if (!hasValues) return null;

            return (
              <div key={fieldValue.id}>
                <h5 className="text-sm font-semibold text-gray-500 dark:text-dark-muted mb-2">
                  {field.label}
                </h5>

                {/* TEXT or NUMBER - single value */}
                {(field.inputType === "TEXT" ||
                  field.inputType === "NUMBER") && (
                  <p className="text-text dark:text-dark-text font-medium">
                    {getValue(values[0])}
                  </p>
                )}

                {/* RADIO or DROPDOWN - single value */}
                {(field.inputType === "RADIO" ||
                  field.inputType === "DROPDOWN") && (
                  <span
                    className="inline-block px-3 py-1.5 rounded-lg text-sm font-medium border"
                    style={{
                      backgroundColor: `${categoryColor}15`,
                      borderColor: `${categoryColor}40`,
                      color: categoryColor,
                    }}
                  >
                    {getValue(values[0])}
                  </span>
                )}

                {/* CHECKBOX or MULTI_SELECT - multiple values */}
                {(field.inputType === "CHECKBOX" ||
                  field.inputType === "MULTI_SELECT") && (
                  <div className="flex flex-wrap gap-2">
                    {values.map((val, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium border"
                        style={{
                          backgroundColor: `${categoryColor}15`,
                          borderColor: `${categoryColor}40`,
                          color: categoryColor,
                        }}
                      >
                        {val}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render documents section
  const renderDocuments = () => {
    if (!caregiver.documents || caregiver.documents.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h4 className="text-lg font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
          <FileText size={20} className="text-blue-500" /> Documents
        </h4>
        <div className="bg-background dark:bg-dark-background p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {caregiver.documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-start gap-3 p-3 bg-card dark:bg-dark-surface rounded-lg border border-gray-100 dark:border-dark-border"
              >
                <FileText
                  className="text-primary flex-shrink-0 mt-1"
                  size={18}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-text dark:text-dark-text truncate">
                    {doc.documentType}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </p>
                  {doc.url && (
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline mt-1 inline-block"
                    >
                      View
                    </a>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {doc.isVerified ? (
                    <CheckCircle className="text-green-500" size={18} />
                  ) : (
                    <XCircle
                      className="text-gray-400 dark:text-gray-600"
                      size={18}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render reference information if available
  const renderReferenceSection = () => {
    if (!caregiver.hasReferee) return null;

    return (
      <div className="space-y-4">
        <h4 className="text-lg font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
          <Shield size={20} className="text-purple-500" /> Reference Information
        </h4>
        <div className="bg-background dark:bg-dark-background p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h5 className="text-sm font-semibold text-gray-500 dark:text-dark-muted mb-1">
                Referee Name
              </h5>
              <p className="text-text dark:text-dark-text">
                {getValue(caregiver.refereeName)}
              </p>
            </div>
            <div>
              <h5 className="text-sm font-semibold text-gray-500 dark:text-dark-muted mb-1">
                Referee Phone
              </h5>
              <p className="text-text dark:text-dark-text">
                {getValue(caregiver.refereePhoneNumber)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-dark-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-background dark:bg-dark-surface">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full">
            <div className="flex items-center justify-between w-full sm:w-auto">
              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  <img
                    src={getValue(
                      caregiver.image,
                      "https://via.placeholder.com/150",
                    )}
                    alt={caregiver.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-card dark:border-dark-surface shadow-md"
                  />
                  <div
                    className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 text-white p-1 rounded-full border-2 sm:border-4 border-card dark:border-dark-surface"
                    style={{ backgroundColor: categoryColor }}
                  >
                    <User size={16} />
                  </div>
                </div>
                <div className="sm:hidden">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-xl font-bold text-text dark:text-dark-text line-clamp-1">
                      {getValue(caregiver.name)}
                    </h2>
                    <span
                      className="px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider w-fit"
                      style={{
                        backgroundColor: `${categoryColor}20`,
                        color: categoryColor,
                      }}
                    >
                      {categoryLabel}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 sm:hidden bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-full text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors shadow-sm"
              >
                <X size={20} />
              </button>
            </div>

            <div className="hidden sm:block">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-1">
                <h2 className="text-xl sm:text-2xl font-bold text-text dark:text-dark-text line-clamp-1">
                  {getValue(caregiver.name)}
                </h2>
                <span
                  className="px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-bold uppercase tracking-wider w-fit"
                  style={{
                    backgroundColor: `${categoryColor}20`,
                    color: categoryColor,
                  }}
                >
                  {categoryLabel}
                </span>
              </div>
              <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-sm text-gray-500 dark:text-dark-muted">
                <span className="flex items-center gap-1.5 font-medium text-text dark:text-dark-text">
                  Br {getValue(caregiver.fees, 0)} / mo
                </span>
                <span className="hidden sm:inline w-1 h-1 rounded-full bg-gray-300 dark:bg-dark-border"></span>
                <span className="flex items-center gap-1.5 font-medium text-yellow-600 dark:text-yellow-400">
                  <Star size={14} fill="currentColor" />
                  {Number(getValue(caregiver.rating, 0)).toFixed(1)}
                </span>
                <span className="hidden sm:inline w-1 h-1 rounded-full bg-gray-300 dark:bg-dark-border"></span>
                <span className="flex items-center gap-1.5 font-medium text-blue-600 dark:text-blue-400">
                  <Briefcase size={14} />
                  {getValue(caregiver.experience, 0)} years
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hidden sm:block p-2 sm:p-2.5 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-full text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors shadow-sm self-center"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Left Column: Basic Info */}
            <div className="lg:col-span-1 space-y-4 md:space-y-6">
              {/* Contact & Location */}
              <div className="bg-background dark:bg-dark-surface p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border space-y-3">
                <h4 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Contact Info
                </h4>
                <div className="flex items-start gap-3 text-sm text-text dark:text-dark-text">
                  <Phone
                    size={16}
                    className="text-gray-400 dark:text-dark-muted mt-0.5 shrink-0"
                  />
                  <span className="break-all">
                    {getValue(caregiver.phoneNumber || caregiver.phone)}
                  </span>
                </div>
                {caregiver.alternativephoneNumber && (
                  <div className="flex items-start gap-3 text-sm text-text dark:text-dark-text">
                    <Phone
                      size={16}
                      className="text-gray-400 dark:text-dark-muted mt-0.5 shrink-0"
                    />
                    <span className="break-all">
                      Alt: {getValue(caregiver.alternativephoneNumber)}
                    </span>
                  </div>
                )}
                <div className="flex items-start gap-3 text-sm text-text dark:text-dark-text">
                  <Mail
                    size={16}
                    className="text-gray-400 dark:text-dark-muted mt-0.5 shrink-0"
                  />
                  <span className="break-all">{getValue(caregiver.email)}</span>
                </div>
                <div className="h-px bg-gray-200 dark:bg-dark-border my-2"></div>
                <div className="flex items-start gap-3 text-sm text-text dark:text-dark-text">
                  <MapPin
                    size={16}
                    className="text-gray-400 dark:text-dark-muted mt-0.5 shrink-0"
                  />
                  <span>
                    {getValue(
                      caregiver.address?.line1 || caregiver.location,
                      "Address not specified",
                    )}
                    {caregiver.address?.line2 && (
                      <>
                        ,{" "}
                        <span className="font-medium">
                          {caregiver.address.line2}
                        </span>
                      </>
                    )}
                  </span>
                </div>
                {caregiver.subcity && (
                  <div className="text-xs text-gray-500 dark:text-dark-muted pl-7">
                    Subcity: {getValue(caregiver.subcity)}
                  </div>
                )}
              </div>

              {/* Professional Details */}
              <div className="bg-background dark:bg-dark-surface p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border space-y-3">
                <h4 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Professional Details
                </h4>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Experience
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {getValue(caregiver.experience, 0)} years
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Nationality
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {getValue(caregiver.nationality)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Education
                  </span>
                  <span className="font-bold text-text dark:text-dark-text text-right">
                    {getValue(caregiver.education)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Date of Birth
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {getValue(caregiver.dob)}
                  </span>
                </div>
                <div className="h-px bg-gray-200 dark:bg-dark-border my-2"></div>
                <div>
                  <span className="text-gray-500 dark:text-dark-muted text-xs block mb-1.5">
                    Languages
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {languages.map((lang, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-md text-xs font-medium text-text dark:text-dark-text"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Work Preferences */}
              <div className="bg-background dark:bg-dark-surface p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border space-y-3">
                <h4 className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider mb-2">
                  Work Preferences
                </h4>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Availability
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {getValue(caregiver.available, true)
                      ? "Available"
                      : "Not Available"}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Work Preference
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {getValue(caregiver.date_time_preference_to_work)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500 dark:text-dark-muted">
                    Vetting Status
                  </span>
                  <span className="font-bold text-text dark:text-dark-text">
                    {getValue(caregiver.vetting_status || caregiver.stage)}
                  </span>
                </div>
                {caregiver.onboardedAt && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 dark:text-dark-muted">
                      Onboarded On
                    </span>
                    <span className="font-bold text-text dark:text-dark-text">
                      {new Date(caregiver.onboardedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Middle/Right Column: Category-specific and About */}
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              {/* About Section */}
              <div>
                <h4 className="text-lg font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
                  <FileText size={20} className="text-primary" /> About
                </h4>
                <div className="bg-background dark:bg-dark-background p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border">
                  <p className="text-gray-600 dark:text-dark-muted leading-relaxed whitespace-pre-wrap">
                    {getValue(caregiver.about, "No description available.")}
                  </p>
                </div>
              </div>

              {/* Dynamic Category-specific Fields */}
              {renderDynamicFields()}

              {/* Documents Section */}
              {renderDocuments()}

              {/* Reference Section */}
              {renderReferenceSection()}

              {/* Application Timeline */}
              <div>
                <h4 className="text-lg font-bold text-text dark:text-dark-text mb-3 flex items-center gap-2">
                  <Calendar size={20} className="text-purple-500" /> Application
                  Timeline
                </h4>
                <div className="bg-background dark:bg-dark-background p-4 sm:p-5 rounded-2xl border border-gray-100 dark:border-dark-border">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-card dark:bg-dark-surface rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-dark-muted mb-1">
                        Applied On
                      </div>
                      <div className="font-bold text-text dark:text-dark-text">
                        {caregiver.createdAt
                          ? new Date(caregiver.createdAt).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </div>
                    <div className="text-center p-3 bg-card dark:bg-dark-surface rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-dark-muted mb-1">
                        Days in Process
                      </div>
                      <div className="font-bold text-text dark:text-dark-text">
                        {caregiver.createdAt
                          ? Math.floor(
                              (new Date() - new Date(caregiver.createdAt)) /
                                (1000 * 60 * 60 * 24),
                            )
                          : 0}{" "}
                        days
                      </div>
                    </div>
                    <div className="text-center p-3 bg-card dark:bg-dark-surface rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-dark-muted mb-1">
                        Current Stage
                      </div>
                      <div className="font-bold text-text dark:text-dark-text">
                        {getValue(caregiver.vetting_status || caregiver.stage)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
